import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut, Radar, PolarArea } from 'react-chartjs-2';
import { usePanneauDroit } from '../shell/cornerDock';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  ArcElement, Title, Tooltip, Legend, Filler, RadialLinearScale
);

// Palette data-viz DS « Cosmos » : valeurs hex reflétant --chart-1..8 (chart.js
// peint sur canvas et ne résout pas var(--…), d'où l'usage des hex).
const PURPLE = '#5a4fe0';       // iris (chart-1)
const PURPLE_LIGHT = '#8b5cf6'; // violet (chart-6)
const PURPLE_BG = '#eef0fe';    // iris très clair
const STATUS_COLORS = {
  'Annulé': '#94a3b8', 'Backlog': '#64748b', 'Demande': '#0ea5e9',
  'Prêt à développer': '#8b5cf6', 'En cours': '#f59e0b',
  'Développé': '#10b981', 'Validé': '#22d3ee', 'Terminé': '#10b981', 'Bloqué': '#f43f5e',
};
const TYPE_COLORS = {
  'Évolution': '#10b981', 'Tâche': '#0ea5e9', 'Bug': '#f43f5e', 'Sub-task': '#f59e0b',
  'Support': '#8b5cf6', 'MEP': '#22d3ee', 'Retour client': '#f97316',
  'Retour interne': '#0284c7', 'Tâche préventive': '#06b6d4',
  'Tâche de gestion': '#5a4fe0', 'Recommandation': '#6d63ea', 'Risque': '#e11d48',
};
const CHART_PALETTE = [
  '#5a4fe0', '#22d3ee', '#10b981', '#f59e0b', '#f43f5e',
  '#8b5cf6', '#0ea5e9', '#f97316',
];

function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const k = item[key] || 'Non défini';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
}

function pickColors(labels, colorMap) {
  return labels.map((l, i) => colorMap[l] || CHART_PALETTE[i % CHART_PALETTE.length]);
}

// ─── KPI Card ───
const KpiCard = ({ label, value, icon, trend, trendUp, color = 'purple' }) => {
  const colorMap = {
    purple: 'bg-iris-50 text-iris-700 border-iris-200',
    blue: 'bg-sky-50 text-sky-700 border-sky-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`rounded-xl border p-5 flex flex-col gap-2 ${colorMap[color] || colorMap.purple} transition-shadow hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium opacity-80">{label}</span>
        <span className="text-xl">{icon}</span>
      </div>
      <span className="text-3xl font-bold">{value}</span>
      {trend !== undefined && (
        <span className={`text-xs font-medium ${trendUp ? 'text-emerald-600' : 'text-rose-500'}`}>
          {trendUp ? '↑' : '↓'} {trend}
        </span>
      )}
    </div>
  );
};

// ─── Chart Card wrapper ───
const ChartCard = ({ title, children, onRemove, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col ${className}`}>
    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
      <h3 className="font-semibold text-slate-700 text-sm">{title}</h3>
      {onRemove && (
        <button onClick={onRemove} className="text-slate-400 hover:text-red-500 transition-colors" title="Retirer">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      )}
    </div>
    <div className="p-5 flex-1 flex items-center justify-center min-h-[260px]">{children}</div>
  </div>
);

// ─── Example chart definitions ───
const EXAMPLE_CHARTS = [
  {
    id: 'sprint-burndown',
    name: 'Burndown Sprint',
    description: 'Courbe de progression des tâches restantes sur un sprint de 2 semaines',
    category: 'time',
    render: () => {
      const days = ['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10'];
      return (
        <Line data={{
          labels: days,
          datasets: [
            { label: 'Idéal', data: [40,36,32,28,24,20,16,12,8,0], borderColor: '#cbd5e1', borderDash: [5,5], tension: 0, fill: false, pointRadius: 0 },
            { label: 'Réel', data: [40,38,35,33,28,25,18,14,7,2], borderColor: PURPLE, backgroundColor: 'rgba(90,79,224,0.1)', tension: 0.3, fill: true },
          ],
        }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true, title: { display: true, text: 'Tâches restantes' } } } }} />
      );
    },
  },
  {
    id: 'velocity',
    name: 'Vélocité par Sprint',
    description: 'Nombre de points/tâches complétés par sprint',
    category: 'performance',
    render: () => (
      <Bar data={{
        labels: ['Sprint 1','Sprint 2','Sprint 3','Sprint 4','Sprint 5','Sprint 6'],
        datasets: [
          { label: 'Engagé', data: [24,28,26,30,32,28], backgroundColor: 'rgba(90,79,224,0.3)', borderColor: PURPLE, borderWidth: 1 },
          { label: 'Complété', data: [22,26,28,27,30,26], backgroundColor: PURPLE },
        ],
      }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } }} />
    ),
  },
  {
    id: 'cumulative-flow',
    name: 'Flux Cumulatif',
    description: 'Diagramme de flux cumulatif par statut sur 8 semaines',
    category: 'time',
    render: () => {
      const weeks = ['S1','S2','S3','S4','S5','S6','S7','S8'];
      return (
        <Line data={{
          labels: weeks,
          datasets: [
            { label: 'Terminé', data: [5,12,20,30,42,55,68,82], backgroundColor: 'rgba(16,185,129,0.4)', borderColor: '#10b981', fill: true },
            { label: 'En cours', data: [8,15,24,34,48,60,74,88], backgroundColor: 'rgba(245,158,11,0.4)', borderColor: '#f59e0b', fill: true },
            { label: 'Backlog', data: [20,25,32,40,55,65,80,95], backgroundColor: 'rgba(107,114,128,0.4)', borderColor: '#6b7280', fill: true },
          ],
        }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { stacked: true, beginAtZero: true } } }} />
      );
    },
  },
  {
    id: 'team-workload',
    name: 'Charge par Membre',
    description: 'Répartition de la charge de travail par membre d\'équipe',
    category: 'team',
    render: () => (
      <Bar data={{
        labels: ['Alice','Bob','Charlie','Diana','Eve','Frank'],
        datasets: [
          { label: 'En cours', data: [5,3,7,4,6,2], backgroundColor: '#f59e0b' },
          { label: 'Terminé', data: [12,15,9,14,11,8], backgroundColor: '#10b981' },
          { label: 'Bloqué', data: [1,0,2,1,0,3], backgroundColor: '#f43f5e' },
        ],
      }} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { position: 'bottom' } }, scales: { x: { stacked: true, beginAtZero: true }, y: { stacked: true } } }} />
    ),
  },
  {
    id: 'priority-radar',
    name: 'Radar des Priorités',
    description: 'Vue radar de la distribution des priorités par type de tâche',
    category: 'performance',
    render: () => (
      <Radar data={{
        labels: ['Critique','Haute','Moyenne','Basse','Aucune'],
        datasets: [
          { label: 'Bug', data: [8,12,5,2,1], backgroundColor: 'rgba(244,63,94,0.2)', borderColor: '#f43f5e' },
          { label: 'Évolution', data: [2,6,15,8,3], backgroundColor: 'rgba(16,185,129,0.2)', borderColor: '#10b981' },
          { label: 'Support', data: [5,8,10,4,2], backgroundColor: 'rgba(139,92,246,0.2)', borderColor: '#8b5cf6' },
        ],
      }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
    ),
  },
  {
    id: 'time-per-project',
    name: 'Temps par Projet',
    description: 'Répartition du temps passé par projet (heures)',
    category: 'time',
    render: () => (
      <PolarArea data={{
        labels: ['Projet Alpha','Projet Beta','Projet Gamma','Projet Delta','Maintenance'],
        datasets: [{
          data: [120,85,65,45,30],
          backgroundColor: ['rgba(90,79,224,0.6)','rgba(14,165,233,0.6)','rgba(16,185,129,0.6)','rgba(245,158,11,0.6)','rgba(244,63,94,0.6)'],
        }],
      }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
    ),
  },
  {
    id: 'weekly-creation',
    name: 'Création Hebdomadaire',
    description: 'Nombre de tâches créées vs résolues par semaine',
    category: 'time',
    render: () => (
      <Line data={{
        labels: ['Sem 1','Sem 2','Sem 3','Sem 4','Sem 5','Sem 6','Sem 7','Sem 8'],
        datasets: [
          { label: 'Créées', data: [12,8,15,10,18,14,9,11], borderColor: '#0ea5e9', backgroundColor: 'rgba(14,165,233,0.1)', fill: true, tension: 0.4 },
          { label: 'Résolues', data: [10,11,12,13,15,16,12,14], borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)', fill: true, tension: 0.4 },
        ],
      }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } }} />
    ),
  },
  {
    id: 'resolution-time',
    name: 'Temps de Résolution',
    description: 'Temps moyen de résolution par type de tâche (jours)',
    category: 'performance',
    render: () => (
      <Bar data={{
        labels: ['Bug','Évolution','Support','Tâche','MEP','Retour client'],
        datasets: [{
          label: 'Jours (moy.)',
          data: [2.5, 8.3, 1.2, 4.1, 6.7, 3.8],
          backgroundColor: ['#f43f5e','#10b981','#8b5cf6','#0ea5e9','#06b6d4','#f97316'],
          borderRadius: 6,
        }],
      }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, title: { display: true, text: 'Jours' } } } }} />
    ),
  },
];

// ─── Chat Message Component ───
const ChatMessage = ({ message, isUser }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
      isUser
        ? 'bg-iris-600 text-white rounded-br-md'
        : 'bg-slate-100 text-slate-800 rounded-bl-md'
    }`}>
      {message.text}
      {message.chart && (
        <div className="mt-3 bg-white rounded-lg p-3 border">{message.chart}</div>
      )}
    </div>
  </div>
);

// ─── Chat Suggestions ───
const CHAT_SUGGESTIONS = [
  'Montre-moi la répartition des tâches par statut',
  'Quel est le burndown du sprint en cours ?',
  'Affiche la charge de travail par membre',
  'Compare les tâches créées vs résolues ce mois',
  'Temps moyen de résolution par type',
  'Quels projets consomment le plus de temps ?',
];

/**
 * Largeur du panneau de l'assistant, en pixels.
 *
 * Une seule source : le panneau l'applique en style, et la barre d'actions du
 * coin bas-droit s'en sert pour se décaler. Deux valeurs séparées se
 * désynchroniseraient, et la barre reviendrait se poser sur le panneau.
 */
const CHAT_PANEL_WIDTH = 380;

// ─── Main Component ───
export const AnalyticsPage = ({ tasks = [], projects = [], users = [], timeLogs = [] }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardCharts, setDashboardCharts] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { id: 1, text: 'Bonjour ! Je suis l\'assistant d\'analyse Orbit. Demandez-moi de générer des graphiques ou d\'analyser vos données de projet. Par exemple : "Montre-moi la répartition des bugs par priorité"', isUser: false },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatOpen, setChatOpen] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState('all');
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Le panneau de l'assistant est ancré à droite et occupe toute la hauteur :
  // ouvert, il passe SOUS la barre d'actions du coin, dont le bouton
  // « Signaler » se posait alors sur le champ de saisie et sur le bouton
  // d'envoi. Le déclarer décale la barre de la largeur du panneau, comme le
  // font déjà l'assistant global et celui du builder.
  usePanneauDroit('analytics-chat', chatOpen, CHAT_PANEL_WIDTH);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // ─── Computed KPIs ───
  const kpis = useMemo(() => {
    const total = tasks.length;
    const statusGroups = groupBy(tasks, 'status');
    const done = (statusGroups['Terminé'] || 0) + (statusGroups['Validé'] || 0);
    const inProgress = statusGroups['En cours'] || 0;
    const blocked = statusGroups['Bloqué'] || 0;
    const backlog = statusGroups['Backlog'] || 0;
    const totalTimeFromTasks = tasks.reduce((acc, t) => acc + (parseFloat(t.timeSpent) || 0), 0);
    const totalTimeFromLogs = timeLogs.reduce((acc, l) => acc + (parseFloat(l.timeSpent) || 0), 0);
    const totalTime = totalTimeFromTasks || totalTimeFromLogs;
    const totalRaf = tasks.reduce((acc, t) => acc + (parseFloat(t.raf) || 0), 0);
    const totalEstimation = tasks.reduce((acc, t) => acc + (parseFloat(t.estimation) || 0), 0);
    const completionRate = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, inProgress, blocked, backlog, totalTime, totalRaf, totalEstimation, completionRate };
  }, [tasks, timeLogs]);

  // ─── Live charts from real data ───
  const liveCharts = useMemo(() => {
    const statusData = groupBy(tasks, 'status');
    const typeData = groupBy(tasks, 'type');
    const assigneeData = groupBy(tasks, 'assignee');
    const statusLabels = Object.keys(statusData);
    const typeLabels = Object.keys(typeData);
    const assigneeLabels = Object.keys(assigneeData);

    // Time spent per assignee
    const timeByAssignee = {};
    tasks.forEach(t => {
      const name = t.assignee || 'Non assigné';
      timeByAssignee[name] = (timeByAssignee[name] || 0) + (parseFloat(t.timeSpent) || 0);
    });

    // Time spent vs RAF per assignee
    const rafByAssignee = {};
    tasks.forEach(t => {
      const name = t.assignee || 'Non assigné';
      rafByAssignee[name] = (rafByAssignee[name] || 0) + (parseFloat(t.raf) || 0);
    });

    // Tasks per status per assignee (for stacked team chart)
    const teamStatusData = {};
    const allStatuses = ['En cours', 'Terminé', 'Bloqué', 'Backlog', 'Prêt à développer', 'Autre'];
    tasks.forEach(t => {
      const name = t.assignee || 'Non assigné';
      if (!teamStatusData[name]) teamStatusData[name] = {};
      const status = ['En cours','Terminé','Bloqué','Backlog','Prêt à développer'].includes(t.status) ? t.status : 'Autre';
      teamStatusData[name][status] = (teamStatusData[name][status] || 0) + 1;
    });
    const teamLabels = Object.keys(teamStatusData);

    // Profile distribution
    const profileData = groupBy(tasks.filter(t => t.profile), 'profile');

    return {
      statusPie: {
        labels: statusLabels,
        datasets: [{
          data: Object.values(statusData),
          backgroundColor: pickColors(statusLabels, STATUS_COLORS),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
      typeDoughnut: {
        labels: typeLabels,
        datasets: [{
          data: Object.values(typeData),
          backgroundColor: pickColors(typeLabels, TYPE_COLORS),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
      assigneeBar: {
        labels: assigneeLabels,
        datasets: [{
          label: 'Tâches assignées',
          data: Object.values(assigneeData),
          backgroundColor: CHART_PALETTE.slice(0, assigneeLabels.length),
          borderRadius: 6,
        }],
      },
      timeByAssignee: {
        labels: Object.keys(timeByAssignee),
        datasets: [
          { label: 'Temps passé (h)', data: Object.keys(timeByAssignee).map(k => timeByAssignee[k]), backgroundColor: '#5a4fe0', borderRadius: 4 },
          { label: 'RAF (h)', data: Object.keys(timeByAssignee).map(k => rafByAssignee[k] || 0), backgroundColor: '#cbd5e1', borderRadius: 4 },
        ],
      },
      teamStacked: {
        labels: teamLabels,
        datasets: [
          { label: 'En cours', data: teamLabels.map(n => teamStatusData[n]?.['En cours'] || 0), backgroundColor: '#f59e0b' },
          { label: 'Terminé', data: teamLabels.map(n => teamStatusData[n]?.['Terminé'] || 0), backgroundColor: '#10b981' },
          { label: 'Bloqué', data: teamLabels.map(n => teamStatusData[n]?.['Bloqué'] || 0), backgroundColor: '#f43f5e' },
          { label: 'Backlog', data: teamLabels.map(n => teamStatusData[n]?.['Backlog'] || 0), backgroundColor: '#6b7280' },
          { label: 'Prêt à développer', data: teamLabels.map(n => teamStatusData[n]?.['Prêt à développer'] || 0), backgroundColor: '#8b5cf6' },
          { label: 'Autre', data: teamLabels.map(n => teamStatusData[n]?.['Autre'] || 0), backgroundColor: '#cbd5e1' },
        ].filter(ds => ds.data.some(v => v > 0)),
      },
      profileDoughnut: {
        labels: Object.keys(profileData),
        datasets: [{
          data: Object.values(profileData),
          backgroundColor: CHART_PALETTE.slice(0, Object.keys(profileData).length),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
    };
  }, [tasks]);

  // ─── Chat logic (mock AI for now) ───
  const handleChatSend = useCallback(() => {
    const text = chatInput.trim();
    if (!text) return;

    const userMsg = { id: Date.now(), text, isUser: true };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setIsTyping(true);

    setTimeout(() => {
      let responseText = '';
      let responseChart = null;
      const lowerText = text.toLowerCase();

      if (lowerText.includes('statut') || lowerText.includes('répartition')) {
        responseText = `Voici la répartition actuelle des ${tasks.length} tâches par statut :`;
        const statusData = groupBy(tasks, 'status');
        responseChart = (
          <div style={{ height: 200 }}>
            <Doughnut data={{
              labels: Object.keys(statusData),
              datasets: [{ data: Object.values(statusData), backgroundColor: pickColors(Object.keys(statusData), STATUS_COLORS), borderWidth: 2, borderColor: '#fff' }],
            }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { boxWidth: 12, font: { size: 11 } } } } }} />
          </div>
        );
      } else if (lowerText.includes('burndown') || lowerText.includes('sprint')) {
        responseText = 'Voici un exemple de burndown chart pour le sprint en cours :';
        const example = EXAMPLE_CHARTS.find(c => c.id === 'sprint-burndown');
        responseChart = <div style={{ height: 200 }}>{example?.render()}</div>;
      } else if (lowerText.includes('charge') || lowerText.includes('membre') || lowerText.includes('équipe')) {
        responseText = 'Voici la charge de travail par membre de l\'équipe :';
        const assigneeData = groupBy(tasks, 'assignee');
        responseChart = (
          <div style={{ height: 200 }}>
            <Bar data={{
              labels: Object.keys(assigneeData),
              datasets: [{ label: 'Tâches', data: Object.values(assigneeData), backgroundColor: CHART_PALETTE, borderRadius: 4 }],
            }} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } } }} />
          </div>
        );
      } else if (lowerText.includes('créé') || lowerText.includes('résolu') || lowerText.includes('compare')) {
        responseText = 'Voici la tendance des tâches créées vs résolues :';
        const example = EXAMPLE_CHARTS.find(c => c.id === 'weekly-creation');
        responseChart = <div style={{ height: 200 }}>{example?.render()}</div>;
      } else if (lowerText.includes('temps') || lowerText.includes('résolution')) {
        responseText = 'Voici les temps moyens de résolution par type :';
        const example = EXAMPLE_CHARTS.find(c => c.id === 'resolution-time');
        responseChart = <div style={{ height: 200 }}>{example?.render()}</div>;
      } else if (lowerText.includes('projet') && lowerText.includes('temps')) {
        responseText = 'Voici le temps consommé par projet :';
        const example = EXAMPLE_CHARTS.find(c => c.id === 'time-per-project');
        responseChart = <div style={{ height: 200 }}>{example?.render()}</div>;
      } else {
        responseText = 'Je comprends votre demande. Cette fonctionnalité sera bientôt connectée à un modèle IA pour générer des analyses personnalisées. En attendant, essayez des requêtes comme :\n\n• "Répartition des tâches par statut"\n• "Burndown du sprint"\n• "Charge par membre"\n• "Temps de résolution par type"';
      }

      setChatMessages(prev => [...prev, { id: Date.now(), text: responseText, isUser: false, chart: responseChart }]);
      setIsTyping(false);
    }, 800 + Math.random() * 700);
  }, [chatInput, tasks]);

  const addExampleChart = (chart) => {
    if (dashboardCharts.find(c => c.id === chart.id)) return;
    setDashboardCharts(prev => [...prev, chart]);
  };

  const removeChart = (chartId) => {
    setDashboardCharts(prev => prev.filter(c => c.id !== chartId));
  };

  const filteredExamples = galleryFilter === 'all'
    ? EXAMPLE_CHARTS
    : EXAMPLE_CHARTS.filter(c => c.category === galleryFilter);

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
    { id: 'tasks', label: 'Tâches', icon: '✅' },
    { id: 'time', label: 'Temps', icon: '⏱️' },
    { id: 'team', label: 'Équipe', icon: '👥' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-50px)] overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <span className="bg-iris-100 p-2 rounded-lg">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5a4fe0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              </span>
              Analyse
            </h1>
            <p className="text-sm text-slate-500 mt-1">Tableau de bord et rapports du projet</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowGallery(!showGallery)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              Exemples de graphiques
            </button>
            <button
              onClick={() => setChatOpen(!chatOpen)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                chatOpen ? 'bg-iris-600 text-white hover:bg-iris-700' : 'border border-iris-300 text-iris-700 bg-iris-50 hover:bg-iris-100'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Assistant IA
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-iris-50 text-iris-700 border-b-2 border-iris-600'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <span className="mr-1.5">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 transition-all ${chatOpen ? 'pr-0' : ''}`}>
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <KpiCard label="Total Tâches" value={kpis.total} icon="📋" color="purple" />
            <KpiCard label="Terminées" value={kpis.done} icon="✅" trend={`${kpis.completionRate}%`} trendUp={kpis.completionRate > 50} color="green" />
            <KpiCard label="En cours" value={kpis.inProgress} icon="🔄" color="blue" />
            <KpiCard label="Bloquées" value={kpis.blocked} icon="🚫" color="red" />
            <KpiCard label="Backlog" value={kpis.backlog} icon="📥" color="amber" />
            <KpiCard label="Temps passé" value={`${kpis.totalTime}h`} icon="⏱️" color="blue" />
            <KpiCard label="RAF" value={`${kpis.totalRaf}h`} icon="⏳" trend={kpis.totalEstimation > 0 ? `${Math.round((kpis.totalRaf / kpis.totalEstimation) * 100)}% restant` : undefined} trendUp={false} color="amber" />
          </div>

          {/* Charts Grid */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
              <ChartCard title="Répartition par Statut">
                {tasks.length > 0 ? (
                  <div className="w-full h-[260px]">
                    <Doughnut data={liveCharts.statusPie} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { position: 'right', labels: { boxWidth: 12, font: { size: 11 }, padding: 8 } } },
                      cutout: '55%',
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Répartition par Type">
                {tasks.length > 0 ? (
                  <div className="w-full h-[260px]">
                    <Pie data={liveCharts.typeDoughnut} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { position: 'right', labels: { boxWidth: 12, font: { size: 11 }, padding: 8 } } },
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Tâches par Assigné">
                {tasks.length > 0 ? (
                  <div className="w-full h-[260px]">
                    <Bar data={liveCharts.assigneeBar} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ChartCard title="Répartition par Statut">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Bar data={{
                      labels: Object.keys(groupBy(tasks, 'status')),
                      datasets: [{
                        label: 'Tâches',
                        data: Object.values(groupBy(tasks, 'status')),
                        backgroundColor: pickColors(Object.keys(groupBy(tasks, 'status')), STATUS_COLORS),
                        borderRadius: 6,
                      }],
                    }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Répartition par Type">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Bar data={{
                      labels: Object.keys(groupBy(tasks, 'type')),
                      datasets: [{
                        label: 'Tâches',
                        data: Object.values(groupBy(tasks, 'type')),
                        backgroundColor: pickColors(Object.keys(groupBy(tasks, 'type')), TYPE_COLORS),
                        borderRadius: 6,
                      }],
                    }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Priorité des Tâches" className="lg:col-span-2">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    {(() => {
                      const priorities = { 'Critique': 0, 'Haute': 0, 'Moyenne': 0, 'Basse': 0, 'Non définie': 0 };
                      tasks.forEach(t => {
                        const p = t.priority;
                        if (p >= 7) priorities['Critique']++;
                        else if (p >= 5) priorities['Haute']++;
                        else if (p >= 3) priorities['Moyenne']++;
                        else if (p >= 1) priorities['Basse']++;
                        else priorities['Non définie']++;
                      });
                      return (
                        <Bar data={{
                          labels: Object.keys(priorities),
                          datasets: [{
                            label: 'Tâches',
                            data: Object.values(priorities),
                            backgroundColor: ['#e11d48','#f97316','#f59e0b','#10b981','#94a3b8'],
                            borderRadius: 6,
                          }],
                        }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }} />
                      );
                    })()}
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
            </div>
          )}

          {activeTab === 'time' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ChartCard title="Temps passé vs RAF par Membre">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Bar data={liveCharts.timeByAssignee} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { position: 'bottom' } },
                      scales: { y: { beginAtZero: true, title: { display: true, text: 'Heures' } } },
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Estimation vs Réel par Type">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    {(() => {
                      const types = {};
                      tasks.forEach(t => {
                        const tp = t.type || 'Tâche';
                        if (!types[tp]) types[tp] = { estimated: 0, spent: 0 };
                        types[tp].estimated += parseFloat(t.estimation) || 0;
                        types[tp].spent += parseFloat(t.timeSpent) || 0;
                      });
                      const labels = Object.keys(types);
                      return (
                        <Bar data={{
                          labels,
                          datasets: [
                            { label: 'Estimé (h)', data: labels.map(l => types[l].estimated), backgroundColor: 'rgba(90,79,224,0.3)', borderColor: '#5a4fe0', borderWidth: 1 },
                            { label: 'Réel (h)', data: labels.map(l => types[l].spent), backgroundColor: '#5a4fe0', borderRadius: 4 },
                          ],
                        }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true, title: { display: true, text: 'Heures' } } } }} />
                      );
                    })()}
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Burndown Sprint (Exemple)">
                <div className="w-full h-[300px]">{EXAMPLE_CHARTS.find(c => c.id === 'sprint-burndown')?.render()}</div>
              </ChartCard>
              <ChartCard title="Création vs Résolution (Exemple)">
                <div className="w-full h-[300px]">{EXAMPLE_CHARTS.find(c => c.id === 'weekly-creation')?.render()}</div>
              </ChartCard>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ChartCard title="Charge par Membre (statuts)">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Bar data={liveCharts.teamStacked} options={{
                      responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                      plugins: { legend: { position: 'bottom' } },
                      scales: { x: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 } }, y: { stacked: true } },
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Temps passé vs RAF par Membre">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Bar data={liveCharts.timeByAssignee} options={{
                      responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                      plugins: { legend: { position: 'bottom' } },
                      scales: { x: { beginAtZero: true, title: { display: true, text: 'Heures' } } },
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Répartition par Profil">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Doughnut data={liveCharts.profileDoughnut} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { position: 'right', labels: { boxWidth: 12, font: { size: 11 }, padding: 8 } } },
                      cutout: '55%',
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Tâches par Membre">
                {tasks.length > 0 ? (
                  <div className="w-full h-[300px]">
                    <Bar data={liveCharts.assigneeBar} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
                    }} />
                  </div>
                ) : <EmptyChart />}
              </ChartCard>
            </div>
          )}

          {/* User-added charts from gallery */}
          {dashboardCharts.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a4fe0" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Mes graphiques
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {dashboardCharts.map(chart => (
                  <ChartCard key={chart.id} title={chart.name} onRemove={() => removeChart(chart.id)}>
                    <div className="w-full h-[260px]">{chart.render()}</div>
                  </ChartCard>
                ))}
              </div>
            </div>
          )}

          {/* Example Gallery */}
          {showGallery && (
            <div className="mb-6 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-800 text-lg">Galerie de graphiques</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Cliquez sur un graphique pour l'ajouter à votre tableau de bord</p>
                </div>
                <div className="flex gap-2">
                  {['all','time','performance','team'].map(f => (
                    <button
                      key={f}
                      onClick={() => setGalleryFilter(f)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        galleryFilter === f ? 'bg-iris-100 text-iris-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {f === 'all' ? 'Tous' : f === 'time' ? 'Temporel' : f === 'performance' ? 'Performance' : 'Équipe'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 p-6">
                {filteredExamples.map(chart => {
                  const isAdded = dashboardCharts.some(c => c.id === chart.id);
                  return (
                    <button
                      key={chart.id}
                      onClick={() => !isAdded && addExampleChart(chart)}
                      className={`text-left rounded-xl border p-4 transition-all ${
                        isAdded
                          ? 'border-green-300 bg-green-50 cursor-default'
                          : 'border-slate-200 hover:border-iris-300 hover:shadow-md cursor-pointer'
                      }`}
                    >
                      <div className="h-[140px] mb-3">{chart.render()}</div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-800 text-sm">{chart.name}</h4>
                          <p className="text-xs text-slate-500 mt-0.5">{chart.description}</p>
                        </div>
                        {isAdded ? (
                          <span className="text-green-600 text-xs font-medium flex items-center gap-1">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            Ajouté
                          </span>
                        ) : (
                          <span className="text-iris-500 text-xs font-medium">+ Ajouter</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Chat Panel */}
        {chatOpen && (
          <div style={{ width: CHAT_PANEL_WIDTH }} className="flex-shrink-0 bg-white border-l border-slate-200 flex flex-col h-full">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="bg-iris-100 p-1.5 rounded-lg">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5a4fe0" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </span>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm">Assistant IA</h3>
                  <span className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                    En ligne
                  </span>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} aria-label="Fermer l'assistant" className="text-slate-400 hover:text-slate-600 p-1">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4">
              {chatMessages.map(msg => (
                <ChatMessage key={msg.id} message={msg} isUser={msg.isUser} />
              ))}
              {isTyping && (
                <div className="flex justify-start mb-3">
                  <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Suggestions */}
            <div className="px-4 pb-2 flex-shrink-0">
              <div className="flex flex-wrap gap-1.5">
                {CHAT_SUGGESTIONS.slice(0, 3).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setChatInput(s); chatInputRef.current?.focus(); }}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-iris-50 text-iris-600 hover:bg-iris-100 transition-colors border border-iris-100 truncate max-w-[180px]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-slate-100 flex-shrink-0">
              <div className="flex gap-2">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleChatSend()}
                  placeholder="Demandez une analyse..."
                  className="input flex-1 text-sm"
                />
                <button
                  onClick={handleChatSend}
                  disabled={!chatInput.trim()}
                  aria-label="Envoyer le message"
                  className="p-2.5 bg-iris-600 text-white rounded-xl hover:bg-iris-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyChart = () => (
  <div className="flex flex-col items-center justify-center text-slate-400 py-8">
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
    <p className="text-sm mt-2">Aucune donnée disponible</p>
    <p className="text-xs mt-1">Les graphiques s'afficheront avec les données réelles</p>
  </div>
);

export default AnalyticsPage;
